import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminLayout } from './layouts/AdminLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { Login } from './pages/admin/Login';
import { Dashboard } from './pages/admin/Dashboard';
import { Media } from './pages/admin/Media';
import { MediaJobs } from './pages/admin/MediaJobs';
import { PagesList } from './pages/admin/PagesList';
import { PageEdit } from './pages/admin/PageEdit';
import { Menus } from './pages/admin/Menus';

// OLE Admin Pages
import { OlePagesList } from './pages/admin/ole/OlePagesList';
import OleContactMessagesList from './pages/admin/ole/OleContactMessagesList';
import { OlePageEdit } from './pages/admin/ole/OlePageEdit';
import { OleMenus } from './pages/admin/ole/OleMenus';
import { OleContactInfoSettings } from './pages/admin/ole/OleContactInfoSettings';
import { OleSiteConfigSettings } from './pages/admin/ole/OleSiteConfigSettings';
import { OleSlidersList } from './pages/admin/ole/OleSlidersList';
import { OleSliderEdit } from './pages/admin/ole/OleSliderEdit';

import { Courses } from './pages/admin/Courses';
import { Projects } from './pages/admin/Projects';
import PageTemplates from './pages/admin/PageTemplates';
import { Settings } from './pages/admin/Settings';
import { Translations } from './pages/admin/Translations';
import { PostersList } from './pages/admin/PostersList';
import { PosterEdit } from './pages/admin/PosterEdit';
import { LogsList } from './pages/admin/LogsList';
import { LoginLogsList } from './pages/admin/LoginLogsList';
import Sliders from './pages/admin/Sliders';
import SliderEdit from './pages/admin/SliderEdit';
import HomePageSettings from './pages/admin/HomePageSettings';
import FooterSettings from './pages/admin/FooterSettings';
import Donate from './pages/public/Donate';
import Contact from './pages/public/Contact';
import DonateSettings from './pages/admin/DonateSettings';
import DonationsList from './pages/admin/DonationsList';
import { AffiliationsList } from './pages/admin/AffiliationsList';
import NewslettersList from './pages/admin/NewslettersList';
import NewsletterArchive from './pages/public/NewsletterArchive';
import ContactMessagesList from './pages/admin/ContactMessagesList';
import ContactInfoSettings from './pages/admin/ContactInfoSettings';
import UsersList from './pages/admin/UsersList';
import UserDetails from './pages/admin/UserDetails';
import UserCreate from './pages/admin/UserCreate';
import { Mitgliedschaften } from './pages/public/Mitgliedschaften';
import { Forms } from './pages/admin/Forms';
import { FormSubmissions } from './pages/admin/FormSubmissions';
import { FormBuilder } from './pages/admin/FormBuilder';
import { NotificationsList } from './pages/admin/NotificationsList';
import { Home } from './pages/public/Home';
import { DynamicPage } from './pages/public/DynamicPage';
import { Veranstaltungen } from './pages/public/Veranstaltungen';
import { YouthActivities } from './pages/public/YouthActivities';
import { PosterDetail } from './pages/public/PosterDetail';
import { Kursangebot } from './pages/public/Kursangebot';
import { CourseDetail } from './pages/public/CourseDetail';
import { Projekte } from './pages/public/Projekte';
import { ProjectDetail } from './pages/public/ProjectDetail';
import ScrollToTop from './components/common/ScrollToTop';

// Initialize React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

export const App = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <Routes>
                            {/* Public/Website Routes */}
                            <Route element={<PublicLayout />}>
                                <Route path="/" element={<Home />} />
                                <Route path="/veranstaltungen" element={<Veranstaltungen />} />
                                <Route path="/veranstaltungen/youth" element={<YouthActivities />} />
                                <Route path="/veranstaltungen/:slug" element={<PosterDetail />} />
                                <Route path="/kursangebot" element={<Kursangebot />} />
                                <Route path="/kurse/:slug" element={<CourseDetail />} />
                                <Route path="/projekte" element={<Projekte />} />
                                <Route path="/projekte/:slug" element={<ProjectDetail />} />
                                <Route path="/mitgliedschaften" element={<Mitgliedschaften />} />
                                <Route path="/donate" element={<Donate />} />
                                <Route path="/spenden" element={<Navigate to="/donate" replace />} />
                                <Route path="/kontakt" element={<Contact />} />
                                <Route path="/newsletters" element={<NewsletterArchive />} />
                                <Route path="/newsletter-archiv" element={<NewsletterArchive />} />
                                {/* Dynamic Page route placed at root level, matching any /slug string not caught by /admin */}
                                <Route path="/:slug" element={<DynamicPage />} />
                            </Route>

                            {/* Admin Routes */}
                            <Route path="/admin/login" element={<Login />} />

                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<Dashboard />} />
                                <Route path="pages" element={<PagesList />} />
                                <Route path="pages/create" element={<PageEdit />} />
                                <Route path="pages/edit/:id" element={<PageEdit />} />
                                
                                {/* OLE Admin Routes */}
                                <Route path="ole/pages" element={<OlePagesList />} />
                                <Route path="ole/pages/create" element={<OlePageEdit />} />
                                <Route path="ole/pages/edit/:id" element={<OlePageEdit />} />
                                <Route path="ole/menus" element={<OleMenus />} />
                                <Route path="ole/contact-info" element={<OleContactInfoSettings />} />
                                <Route path="ole/site-config" element={<OleSiteConfigSettings />} />
                                <Route path="ole/contact-messages" element={<OleContactMessagesList />} />
                                <Route path="ole/sliders" element={<OleSlidersList />} />
                                <Route path="ole/sliders/create" element={<OleSliderEdit />} />
                                <Route path="ole/sliders/edit/:id" element={<OleSliderEdit />} />

                                <Route path="posters" element={<PostersList />} />
                                <Route path="posters/create" element={<PosterEdit />} />
                                <Route path="posters/edit/:id" element={<PosterEdit />} />
                                <Route path="menus" element={<Menus />} />
                                <Route path="page-templates" element={<PageTemplates />} />
                                <Route path="courses" element={<Courses />} />
                                <Route path="projects" element={<Projects />} />
                                <Route path="media" element={<Media />} />
                                <Route path="media/webp-job" element={<MediaJobs />} />
                                <Route path="logs" element={<LogsList />} />
                                <Route path="login-logs" element={<LoginLogsList />} />
                                <Route path="sliders" element={<Sliders />} />
                                <Route path="sliders/:id" element={<SliderEdit />} />
                                <Route path="homepage-settings" element={<HomePageSettings />} />
                                <Route path="footer-settings" element={<FooterSettings />} />
                                <Route path="donate-settings" element={<DonateSettings />} />
                                <Route path="affiliations" element={<AffiliationsList />} />
                                <Route path="newsletters" element={<NewslettersList />} />
                                <Route path="donations" element={<DonationsList />} />
                                <Route path="contact-messages" element={<ContactMessagesList />} />
                                <Route path="contact-info" element={<ContactInfoSettings />} />
                                <Route path="users" element={<UsersList />} />
                                <Route path="users/create" element={<UserCreate />} />
                                <Route path="users/:id" element={<UserDetails />} />
                                <Route path="forms" element={<Forms />} />
                                <Route path="forms/:id/submissions" element={<FormSubmissions />} />
                                <Route path="forms/:id/builder" element={<FormBuilder />} />
                                <Route path="notifications" element={<NotificationsList />} />
                                <Route path="settings" element={<Settings />} />
                                <Route path="translations" element={<Translations />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/admin" replace />} />
                        </Routes>
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
};

export default App;
